import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { DataView as PrimeDataList } from 'primeng/dataview';
import { DatagridService } from '../../shared/index';
import { UserService } from '../../users/shared/user.service';
import { PagedResult } from '../../shared/model/generic-paged-result.model';
import { InvoiceService } from '../shared/invoice/invoice.service';
import { InvoiceAdvancedSearch } from '../shared/invoice/invoice-advanced-search.model';
import { InvoiceAdvancedSearchResult } from '../shared/invoice/invoice-advanced-search-result.model';
import { InvoiceSelectorFormComponent } from './form/invoice-selector-form.component';

/**
 * admin mass comm messages
 */
@Component({
  selector: 'wl-invoice-selector',
  templateUrl: 'invoice-selector.component.html',
  styleUrls: [],
  providers: [],
    standalone: false
})
export class InvoiceSelectorComponent implements OnInit {
  @Input() public searchParams: InvoiceAdvancedSearch = new InvoiceAdvancedSearch();
  @Output('result') public resultEmitter: EventEmitter<PagedResult<Array<InvoiceAdvancedSearchResult>>> = new EventEmitter<
    PagedResult<Array<InvoiceAdvancedSearchResult>>
  >();
  @Output('searching') public searchingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('accessDenied') public accessDeniedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('searchReultsGrid', { static: false }) private _searchReultsGrid: PrimeDataList;
  @ViewChild('invoiceSelectorForm', { static: false }) private _invoiceSelectorForm: InvoiceSelectorFormComponent;

  public searching: boolean = false;
  private _accessDenied: boolean = false;
  public searchResult: PagedResult<Array<InvoiceAdvancedSearchResult>> = new PagedResult<Array<InvoiceAdvancedSearchResult>>();
  public hasSearched: boolean = false;

  /**
   * inject
   * @param _userService
   * @param _router
   */
  public constructor(
    private _userService: UserService,
    private _invoiceService: InvoiceService,
    private _datagridService: DatagridService
  ) {}

  /**
   * on init
   */
  public ngOnInit(): void {
    // make sure they can use this
    this._userService.getCurrentUserSecurityElementAccessLevel('Revenue_Module').then((userAccessLevel: number) => {
      this.setAccessDeniedFlag(userAccessLevel < 1);
    });

    let pageEvent = { first: 0, rows: 10 };
    this._searchReultsGrid.paginate(pageEvent);
  }

  /**
   * lazyload of the invoice selector grid
   */
  public lazyLoadSearchResults($event: any): void {
    // grid wants to load as soon as it's created. don't let it. let it only load when someone clicks "search"
    if (!this.hasSearched) return;

    (<any>this._searchReultsGrid).first = 0;

    this._datagridService.mapDatagridPagingToPagedSearch($event, this.searchParams);

    if ($event) {
      this.searchParams.PageNumber = $event.first / Math.max(1, $event.rows) + 1;
      this.searchParams.PageSize = 10;
    }

    if (!this.searching) this.search();
  }

  /**
   * start search
   */
  public search(): Promise<void> {
    this.hasSearched = true;
    return this.getInvoices();
  }

  /**
   * resets the results grid
   */
  public resetResultGrid(): void {
    // make it look like a new form
    if (this.searchParams) this.searchParams.OrderBy = 'ProfileReportName:asc';

    this._invoiceSelectorForm.resetForm();
    this.hasSearched = false;

    setTimeout(() => {
      this.searchResult.Result = [];
      this.searchResult.TotalCount = 0;
      this.searchResult.TotalPages = 0;
      this.searchResult.CurrentPageNumber = 1;
      //this._searchReultsGrid.sortField = "ProfileReportName";
      //this._searchReultsGrid.sortOrder = 1;
    });
  }

  /**
   * get invoices
   */
  private getInvoices(): Promise<void> {
    return this._invoiceSelectorForm.search();
  }

  /**
   * change searching flag and emit change notification if different
   * @param value
   */
  private setSearchingFlag(value: boolean): void {
    let oldValue: boolean = this.searching;
    this.searching = value;

    if (oldValue != this.searching) this.searchingEmitter.emit(this.searching);
  }

  /**
   * change searching flag and emit change notification if different
   * @param value
   */
  private setAccessDeniedFlag(value: boolean): void {
    let oldValue: boolean = this._accessDenied;
    this._accessDenied = value;

    if (oldValue != this._accessDenied) this.accessDeniedEmitter.emit(this._accessDenied);
  }

  /**
   * handle when the form started the search. also notify the parent component if they're listening.
   * @param searching
   */
  public handleSearching(searching: boolean) {
    // pass through
    this.setSearchingFlag(!!searching);
  }

  /**
   * handle when the form thinks access is denied. also notify the parent component if they're listening.
   * @param accessDenied
   */
  public handleAccessDenied(accessDenied: boolean) {
    // pass through
    this.setAccessDeniedFlag(!!accessDenied);
  }

  public handleSearchResultReturned(result: PagedResult<Array<InvoiceAdvancedSearchResult>>) {
    this.searchResult = result;
    this.resultEmitter.emit(result);
  }
}
